import React from 'react';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { zoom, isIdentity, IDENTITY } from '../../../lib/photoManualTransform';
import { transformString } from '../../../lib/photoRender';
import {
  OnPhotoControls, ZoomBadge, PanelBackdrop, GridOverlay, PhotoLabel,
  canonicalImgStyle, formatMetricLine,
} from './PanelBits';
import { EmptySlot } from './SideBySidePair';

// One pair as a single wipe panel. sliderPos is owned by the caller so two
// panels can share one wipe position.
export default function SliderPanel({ pair, sliderPos, onSliderPos, gridOn, labels, testIdSuffix = '', computing = false }) {
  const { before, after } = pair;
  if (!before.url || !after.url) {
    return <EmptySlot label={labels.missing} testId={`cmp-panel-slider${testIdSuffix}`} />;
  }
  return (
    <div data-testid={`cmp-panel-slider${testIdSuffix}`}
         className="relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black select-none
                    ring-1 ring-white/10 shadow-xl">
      <PanelBackdrop url={after.url} />
      <img src={after.url} alt="" data-testid="cmp-img"
           style={canonicalImgStyle(after.canonical)} draggable={false} />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
        <img src={before.url} alt="" data-testid="cmp-img"
             style={canonicalImgStyle(before.canonical, transformString(null, before.manual))}
             draggable={false} />
      </div>

      {gridOn && <GridOverlay />}

      <PhotoLabel position="top-left" tone="dark"
                  metric={formatMetricLine(before.weightKg, before.bodyFat)} label={labels.before} />
      <PhotoLabel position="top-right" tone="accent"
                  metric={formatMetricLine(after.weightKg, after.bodyFat)} label={labels.after} />

      <div className="absolute inset-y-0 pointer-events-none"
           style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
        <div className="h-full w-0.5 bg-white/90 shadow-[0_0_8px_rgba(0,0,0,0.5)]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white
                        shadow-lg flex items-center justify-center">
          <ArrowLeftRight className="h-3.5 w-3.5 text-gray-700" />
        </div>
      </div>

      <input type="range" min="0" max="100" value={sliderPos}
             onChange={(e) => onSliderPos(Number(e.target.value))}
             aria-label={labels.wipePosition}
             className="absolute inset-0 w-full h-full opacity-0 cursor-col-resize z-10" />

      <ZoomBadge manual={before.manual} />
      <OnPhotoControls
        onZoomOut={() => before.setManual((m) => zoom(m, -1, before.canonical))}
        onZoomIn={() => before.setManual((m) => zoom(m, +1, before.canonical))}
        onAuto={before.autoMatch}
        autoTitle={labels.mismatch}
        onReset={isIdentity(before.manual) ? null : () => before.setManual(IDENTITY)}
      />

      {computing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      )}
    </div>
  );
}
